import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Login() {
    const { login } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    async function submit(e) {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const user = await login(email, password);
            toast.success(`Welcome back, ${user.name.split(" ")[0]}!`);
            const dest = loc.state?.from || (user.role === "admin" ? "/admin" : "/profile");
            nav(dest);
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || "Invalid email or password");
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="min-h-[80vh] grid md:grid-cols-2" data-testid="login-page">
            <div className="hidden md:block bg-ink text-white p-12 flex flex-col justify-between relative">
                <img src="https://images.pexels.com/photos/934350/pexels-photo-934350.jpeg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
                <div className="relative z-10">
                    <div className="flex items-center gap-2.5">
                        <img src="/logo.png" alt="Peppa Target" className="h-12 w-auto bg-white rounded p-0.5" />
                        <span className="font-display font-bold text-lg">Peppa Target</span>
                    </div>
                </div>
                <div className="relative z-10">
                    <h1 className="font-display text-4xl font-bold leading-tight">Welcome back.</h1>
                    <p className="mt-3 text-white/70 max-w-sm">Sign in to track orders, manage applications, and access your account.</p>
                </div>
            </div>
            <div className="flex items-center justify-center px-4 py-16">
                <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
                    <h2 className="font-display text-3xl font-bold mb-2">Sign in</h2>
                    <p className="text-sm text-muted-foreground mb-8">New here? <Link to="/register" className="text-primary font-semibold">Create an account</Link></p>
                    {error && <div className="mb-4 bg-primary/10 text-primary text-sm p-3 border-l-4 border-primary" data-testid="login-error">{error}</div>}
                    <label className="block mb-4">
                        <span className="text-xs uppercase tracking-wider text-muted-foreground">Email</span>
                        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full border-b border-border px-0 py-2 focus:outline-none focus:border-primary" data-testid="login-email" />
                    </label>
                    <label className="block mb-6">
                        <span className="text-xs uppercase tracking-wider text-muted-foreground">Password</span>
                        <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 w-full border-b border-border px-0 py-2 focus:outline-none focus:border-primary" data-testid="login-password" />
                    </label>
                    <button type="submit" disabled={loading} className="w-full bg-primary text-white py-3 font-semibold hover:bg-primary-hover disabled:opacity-50" data-testid="login-submit">
                        {loading ? "Signing in…" : "Sign in"}
                    </button>
                    <div className="text-xs text-muted-foreground mt-6 text-center">
                        Demo: <code className="font-mono">customer@peppatarget.com / Customer@123</code>
                    </div>
                </form>
            </div>
        </div>
    );
}
